/**
 * Mock data simulating the enriched RateHawkSearchResult[]
 * returned by the stays-search edge function after merging
 * SERP (/search/serp/geo/) + Content API (/hotel_content_by_ids).
 *
 * Three realistic hotels in Rome (lat 41.9028, lng 12.4964).
 */

import type { RateHawkSearchResult } from "@/lib/builder/ratehawk/types";

function makePaymentType(
  showAmount: string,
  currency: string,
  freeBefore: string | null,
  isPenalized = false,
) {
  const amount = parseFloat(showAmount);
  const penaltyFull = isPenalized
    ? [
        {
          start_at: null,
          end_at: null,
          amount_show: showAmount,
          amount_charge: (amount * 120).toFixed(2),
          commission_info: {
            show: { amount_gross: (amount * 1.02).toFixed(2), amount_net: showAmount, amount_commission: (amount * 0.02).toFixed(2) },
            charge: { amount_gross: (amount * 122.4).toFixed(2), amount_net: (amount * 120).toFixed(2), amount_commission: (amount * 2.4).toFixed(2) },
          },
        },
      ]
    : [
        {
          start_at: null,
          end_at: freeBefore,
          amount_show: "0.00",
          amount_charge: "0.00",
          commission_info: {
            show: { amount_gross: "0.00", amount_net: "0.00", amount_commission: "0.00" },
            charge: { amount_gross: "0.00", amount_net: "0.00", amount_commission: "0.00" },
          },
        },
        {
          start_at: freeBefore,
          end_at: null,
          amount_show: showAmount,
          amount_charge: (amount * 120).toFixed(2),
          commission_info: {
            show: { amount_gross: (amount * 1.02).toFixed(2), amount_net: showAmount, amount_commission: (amount * 0.02).toFixed(2) },
            charge: { amount_gross: (amount * 122.4).toFixed(2), amount_net: (amount * 120).toFixed(2), amount_commission: (amount * 2.4).toFixed(2) },
          },
        },
      ];

  return {
    amount: (amount * 120).toFixed(2),
    show_amount: showAmount,
    currency_code: "ILS",
    show_currency_code: currency,
    by: null,
    is_need_credit_card_data: false,
    is_need_cvc: false,
    type: "deposit" as const,
    vat_data: { included: false, applied: false, amount: "0.00", currency_code: "USD", value: "0.00" },
    tax_data: { taxes: [] },
    perks: {},
    commission_info: {
      show: {
        amount_gross: (amount * 1.02).toFixed(2),
        amount_net: showAmount,
        amount_commission: (amount * 0.02).toFixed(2),
      },
      charge: {
        amount_gross: (amount * 122.4).toFixed(2),
        amount_net: (amount * 120).toFixed(2),
        amount_commission: (amount * 2.4).toFixed(2),
      },
    },
    cancellation_penalties: {
      policies: penaltyFull,
      free_cancellation_before: freeBefore,
    },
    recommended_price: null,
  };
}

function makeRate(overrides: {
  match_hash: string;
  search_hash: string;
  meal: string;
  showAmount: string;
  currency: string;
  roomName: string;
  bedsLabel: string;
  bedType: string;
  amenities: string[];
  freeBefore: string | null;
  isPenalized?: boolean;
}) {
  const {
    match_hash, search_hash, meal, showAmount, currency,
    roomName, bedsLabel, bedType, amenities, freeBefore, isPenalized = false,
  } = overrides;
  const nights = 3;
  const perNight = (parseFloat(showAmount) / nights).toFixed(2);
  return {
    match_hash,
    search_hash,
    daily_prices: Array(nights).fill(perNight),
    meal,
    meal_data: {
      value: meal,
      has_breakfast: meal !== "nomeal",
      no_child_meal: false,
    },
    payment_options: {
      payment_types: [makePaymentType(showAmount, currency, freeBefore, isPenalized)],
    },
    bar_rate_price_data: null,
    rg_ext: { class: 3, quality: 5, sex: 0, bathroom: 2, bedding: 3, family: 0, capacity: 2, club: 0, bedrooms: 0, balcony: 0, view: 0, floor: 0 },
    room_name: roomName,
    room_name_info: { original_rate_name: roomName },
    serp_filters: ["has_bathroom"],
    sell_price_limits: null,
    allotment: 5,
    amenities_data: amenities,
    any_residency: false,
    deposit: null,
    no_show: null,
    room_data_trans: {
      main_room_type: roomName,
      main_name: roomName,
      bathroom: "private bathroom",
      bedding_type: bedsLabel,
      misc_room_type: null,
      beds: [{ bed: bedType, count: 1 }],
    },
    legal_info: null,
    is_package: false,
  };
}

// ─── Hotel 1: Luxurious 5-star ────────────────────────────────────────────────

const hotel1Rates = [
  makeRate({
    match_hash: "m-rh-001-std",
    search_hash: "sr-rh-search-001",
    meal: "breakfast",
    showAmount: "420.00",
    currency: "EUR",
    roomName: "Classic Double Room",
    bedsLabel: "queen bed",
    bedType: "double",
    amenities: ["queen-bed", "non-smoking", "air-conditioning"],
    freeBefore: "2026-07-15T12:00:00",
  }),
  makeRate({
    match_hash: "m-rh-001-deluxe",
    search_hash: "sr-rh-search-001",
    meal: "all-inclusive",
    showAmount: "695.00",
    currency: "EUR",
    roomName: "Deluxe Suite with View",
    bedsLabel: "king bed",
    bedType: "double",
    amenities: ["king-bed", "balcony", "sea-view", "mini-bar", "non-smoking"],
    freeBefore: "2026-07-10T12:00:00",
  }),
];

const hotel1: RateHawkSearchResult = {
  id: "hotel_eden_roma",
  hid: 1001001,
  kind: "Hotel",
  name: "Hotel Eden Roma",
  address: "Via Ludovisi 49, Roma",
  cityName: "Roma",
  countryCode: "IT",
  latitude: 41.9062,
  longitude: 12.4875,
  starRating: 5,
  coverPhoto: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800",
  photos: [
    "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800",
    "https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800",
    "https://images.unsplash.com/photo-1455587734955-081b22074882?w=800",
    "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=800",
  ],
  description:
    "Elegante hotel storico nel cuore di Roma, a due passi da Villa Borghese. Offre una terrazza panoramica con vista su tutta la città, piscina riscaldata e ristorante stellato. Camere arredate in stile classico italiano con marmi originali e affreschi.",
  amenities: ["has_wifi", "has_parking", "has_pool", "has_spa", "has_restaurant", "has_gym", "has_concierge"],
  checkInTime: "15:00",
  checkOutTime: "12:00",
  cheapestRate: hotel1Rates[0],
  cheapestShowAmount: "420.00",
  cheapestShowCurrency: "EUR",
  meals: [...new Set(hotel1Rates.map((r) => r.meal_data?.value ?? r.meal))],
};

// ─── Hotel 2: Boutique 4-star ─────────────────────────────────────────────────

const hotel2Rates = [
  makeRate({
    match_hash: "m-rh-002-std",
    search_hash: "sr-rh-search-001",
    meal: "nomeal",
    showAmount: "185.00",
    currency: "EUR",
    roomName: "Standard Double Room",
    bedsLabel: "full double bed",
    bedType: "double",
    amenities: ["double-bed", "non-smoking", "desk"],
    freeBefore: "2026-07-18T14:00:00",
  }),
  makeRate({
    match_hash: "m-rh-002-brkf",
    search_hash: "sr-rh-search-001",
    meal: "breakfast",
    showAmount: "215.00",
    currency: "EUR",
    roomName: "Standard Double Room",
    bedsLabel: "full double bed",
    bedType: "double",
    amenities: ["double-bed", "non-smoking", "desk"],
    freeBefore: "2026-07-18T14:00:00",
  }),
  makeRate({
    match_hash: "m-rh-002-sup",
    search_hash: "sr-rh-search-001",
    meal: "breakfast",
    showAmount: "260.00",
    currency: "EUR",
    roomName: "Superior Room with Terrace",
    bedsLabel: "king bed",
    bedType: "double",
    amenities: ["king-bed", "terrace", "city-view", "non-smoking"],
    freeBefore: null,
    isPenalized: true,
  }),
];

const hotel2: RateHawkSearchResult = {
  id: "boutique_trastevere",
  hid: 1001002,
  kind: "Hotel",
  name: "Boutique Trastevere",
  address: "Via della Lungaretta 87, Trastevere, Roma",
  cityName: "Roma",
  countryCode: "IT",
  latitude: 41.8893,
  longitude: 12.4707,
  starRating: 4,
  coverPhoto: "https://images.unsplash.com/photo-1578683010236-d716f9a3f461?w=800",
  photos: [
    "https://images.unsplash.com/photo-1578683010236-d716f9a3f461?w=800",
    "https://images.unsplash.com/photo-1611892440504-42a792e24d32?w=800",
    "https://images.unsplash.com/photo-1563911302283-d2bc129e7570?w=800",
  ],
  description:
    "Piccolo hotel di charme nel quartiere Trastevere, uno dei borghi più romantici di Roma. Edificio restaurato del XVIII secolo con cortile interno e terrazza panoramica. A pochi passi da Santa Maria in Trastevere e dalla vivace vita notturna del quartiere.",
  amenities: ["has_wifi", "has_restaurant", "has_bar", "has_24h_front_desk"],
  checkInTime: "14:00",
  checkOutTime: "11:00",
  cheapestRate: hotel2Rates[0],
  cheapestShowAmount: "185.00",
  cheapestShowCurrency: "EUR",
  meals: [...new Set(hotel2Rates.map((r) => r.meal_data?.value ?? r.meal))],
};

// ─── Hotel 3: Budget 3-star ───────────────────────────────────────────────────

const hotel3Rates = [
  makeRate({
    match_hash: "m-rh-003-twin",
    search_hash: "sr-rh-search-001",
    meal: "nomeal",
    showAmount: "110.00",
    currency: "EUR",
    roomName: "Twin Room",
    bedsLabel: "2 single beds",
    bedType: "single",
    amenities: ["twin-beds", "shared-bathroom"],
    freeBefore: "2026-07-20T14:00:00",
  }),
  makeRate({
    match_hash: "m-rh-003-dbl",
    search_hash: "sr-rh-search-001",
    meal: "breakfast",
    showAmount: "130.00",
    currency: "EUR",
    roomName: "Double Room with Breakfast",
    bedsLabel: "double bed",
    bedType: "double",
    amenities: ["double-bed", "private-bathroom", "non-smoking"],
    freeBefore: null,
    isPenalized: true,
  }),
];

const hotel3: RateHawkSearchResult = {
  id: "pensione_campo_de_fiori",
  hid: 1001003,
  kind: "Hostel",
  name: "Pensione Campo de' Fiori",
  address: "Via del Biscione 6, Roma",
  cityName: "Roma",
  countryCode: "IT",
  latitude: 41.8956,
  longitude: 12.4719,
  starRating: 3,
  coverPhoto: "https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800",
  photos: [
    "https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800",
    "https://images.unsplash.com/photo-1586611292717-f828b167408c?w=800",
  ],
  description:
    "Pensione accogliente a due passi da Campo de' Fiori. Ideale per chi vuole vivere Roma da vero romano, nel cuore del centro storico. Colazione inclusa in alcune tariffe, terrazza comune con vista sui tetti.",
  amenities: ["has_wifi", "has_breakfast"],
  checkInTime: "14:00",
  checkOutTime: "10:30",
  cheapestRate: hotel3Rates[0],
  cheapestShowAmount: "110.00",
  cheapestShowCurrency: "EUR",
  meals: [...new Set(hotel3Rates.map((r) => r.meal_data?.value ?? r.meal))],
};

// ─── Export ───────────────────────────────────────────────────────────────────

export const RATEHAWK_SEARCH_MOCK: RateHawkSearchResult[] = [hotel1, hotel2, hotel3];
